import { z } from "zod";

export const CORE_WEB_VITALS_FIELD_SOURCE =
  "google_chrome_user_experience_report_field" as const;
export const CORE_WEB_VITALS_GOOGLE_LAB_SOURCE =
  "google_page_speed_insights_lab" as const;
export const CORE_WEB_VITALS_SCRAPINGBEE_LAB_SOURCE =
  "scrapingbee_browser_lab" as const;

export const CORE_WEB_VITALS_LAB_SOURCES = [
  CORE_WEB_VITALS_GOOGLE_LAB_SOURCE,
  CORE_WEB_VITALS_SCRAPINGBEE_LAB_SOURCE,
] as const;

// The full source list is the field source plus every lab source, so the
// subset can never drift from the parent.
export const CORE_WEB_VITALS_SOURCES = [
  CORE_WEB_VITALS_FIELD_SOURCE,
  ...CORE_WEB_VITALS_LAB_SOURCES,
] as const;
export const CoreWebVitalsSourceSchema = z.enum(CORE_WEB_VITALS_SOURCES);
export type CoreWebVitalsSource = z.infer<typeof CoreWebVitalsSourceSchema>;

export const CoreWebVitalsLabSourceSchema = z.enum(CORE_WEB_VITALS_LAB_SOURCES);
export type CoreWebVitalsLabSource = z.infer<
  typeof CoreWebVitalsLabSourceSchema
>;

export const CORE_WEB_VITALS_SCOPES = ["page", "website_origin"] as const;
export const CORE_WEB_VITALS_SCOPE = {
  PAGE: "page",
  WEBSITE_ORIGIN: "website_origin",
} as const satisfies Record<string, CoreWebVitalsScope>;
export const CoreWebVitalsScopeSchema = z.enum(CORE_WEB_VITALS_SCOPES);
export type CoreWebVitalsScope = z.infer<typeof CoreWebVitalsScopeSchema>;

export const CORE_WEB_VITALS_FORM_FACTORS = ["PHONE", "DESKTOP"] as const;
export const CORE_WEB_VITALS_FORM_FACTOR = {
  PHONE: "PHONE",
  DESKTOP: "DESKTOP",
} as const satisfies Record<string, CoreWebVitalsFormFactor>;
export const CoreWebVitalsFormFactorSchema = z.enum(
  CORE_WEB_VITALS_FORM_FACTORS,
);
export type CoreWebVitalsFormFactor = z.infer<
  typeof CoreWebVitalsFormFactorSchema
>;

export const CORE_WEB_VITALS_GOOGLE_FIELD_REQUEST_METRICS = [
  "largest_contentful_paint",
  "interaction_to_next_paint",
  "cumulative_layout_shift",
  "first_contentful_paint",
  "experimental_time_to_first_byte",
] as const;
export const CoreWebVitalsGoogleFieldRequestMetricSchema = z.enum(
  CORE_WEB_VITALS_GOOGLE_FIELD_REQUEST_METRICS,
);
export type CoreWebVitalsGoogleFieldRequestMetric = z.infer<
  typeof CoreWebVitalsGoogleFieldRequestMetricSchema
>;

export const CORE_WEB_VITALS_METRIC_NAMES = [
  "largest_contentful_paint",
  "interaction_to_next_paint",
  "cumulative_layout_shift",
  "first_contentful_paint",
  "time_to_first_byte",
  "total_blocking_time",
] as const;
export const CORE_WEB_VITALS_METRIC_NAME = {
  LARGEST_CONTENTFUL_PAINT: "largest_contentful_paint",
  INTERACTION_TO_NEXT_PAINT: "interaction_to_next_paint",
  CUMULATIVE_LAYOUT_SHIFT: "cumulative_layout_shift",
  FIRST_CONTENTFUL_PAINT: "first_contentful_paint",
  TIME_TO_FIRST_BYTE: "time_to_first_byte",
  TOTAL_BLOCKING_TIME: "total_blocking_time",
} as const satisfies Record<string, CoreWebVitalsMetricName>;
export const CoreWebVitalsMetricNameSchema = z.enum(
  CORE_WEB_VITALS_METRIC_NAMES,
);
export type CoreWebVitalsMetricName = z.infer<
  typeof CoreWebVitalsMetricNameSchema
>;

export const CORE_WEB_VITALS_VALUE_UNITS = [
  "milliseconds",
  "layout_shift_milli_units",
] as const;
export const CORE_WEB_VITALS_VALUE_UNIT = {
  MILLISECONDS: "milliseconds",
  LAYOUT_SHIFT_MILLI_UNITS: "layout_shift_milli_units",
} as const satisfies Record<string, CoreWebVitalsValueUnit>;
export const CoreWebVitalsValueUnitSchema = z.enum(CORE_WEB_VITALS_VALUE_UNITS);
export type CoreWebVitalsValueUnit = z.infer<
  typeof CoreWebVitalsValueUnitSchema
>;

export const CORE_WEB_VITALS_STATISTICS = ["p75", "observed"] as const;
export const CORE_WEB_VITALS_STATISTIC = {
  P75: "p75",
  OBSERVED: "observed",
} as const satisfies Record<string, CoreWebVitalsMetricStatistic>;
export const CoreWebVitalsMetricStatisticSchema = z.enum(
  CORE_WEB_VITALS_STATISTICS,
);
export type CoreWebVitalsMetricStatistic = z.infer<
  typeof CoreWebVitalsMetricStatisticSchema
>;

export const CORE_WEB_VITALS_CONFIDENCE_LEVELS = [
  "high",
  "medium",
  "low",
] as const;
export const CORE_WEB_VITALS_CONFIDENCE_LEVEL = {
  HIGH: "high",
  MEDIUM: "medium",
  LOW: "low",
} as const satisfies Record<string, CoreWebVitalsConfidenceLevel>;
export const CoreWebVitalsConfidenceLevelSchema = z.enum(
  CORE_WEB_VITALS_CONFIDENCE_LEVELS,
);
export type CoreWebVitalsConfidenceLevel = z.infer<
  typeof CoreWebVitalsConfidenceLevelSchema
>;

export const CORE_WEB_VITALS_MISS_OR_ERROR_STATUSES = [
  "no_data",
  "error",
] as const;
export const CORE_WEB_VITALS_MISS_OR_ERROR_STATUS = {
  NO_DATA: "no_data",
  ERROR: "error",
} as const satisfies Record<string, CoreWebVitalsMissOrErrorStatus>;
export const CoreWebVitalsMissOrErrorStatusSchema = z.enum(
  CORE_WEB_VITALS_MISS_OR_ERROR_STATUSES,
);

// "success" plus every miss/error status, so the subset cannot drift.
export const CORE_WEB_VITALS_RESULT_STATUSES = [
  "success",
  ...CORE_WEB_VITALS_MISS_OR_ERROR_STATUSES,
] as const;
export const CORE_WEB_VITALS_RESULT_STATUS = {
  SUCCESS: "success",
  ...CORE_WEB_VITALS_MISS_OR_ERROR_STATUS,
} as const satisfies Record<string, CoreWebVitalsResultStatus>;
export const CoreWebVitalsResultStatusSchema = z.enum(
  CORE_WEB_VITALS_RESULT_STATUSES,
);
export type CoreWebVitalsResultStatus = z.infer<
  typeof CoreWebVitalsResultStatusSchema
>;
export type CoreWebVitalsMissOrErrorStatus = z.infer<
  typeof CoreWebVitalsMissOrErrorStatusSchema
>;

export const CORE_WEB_VITALS_ATTEMPT_ROLES = [
  "pageField",
  "originField",
  "googleLab",
  "scrapingbeeLab",
] as const;
export const CORE_WEB_VITALS_ATTEMPT_ROLE = {
  PAGE_FIELD: "pageField",
  ORIGIN_FIELD: "originField",
  GOOGLE_LAB: "googleLab",
  SCRAPINGBEE_LAB: "scrapingbeeLab",
} as const satisfies Record<string, CoreWebVitalsAttemptRole>;
export const CoreWebVitalsAttemptRoleSchema = z.enum(
  CORE_WEB_VITALS_ATTEMPT_ROLES,
);
export type CoreWebVitalsAttemptRole = z.infer<
  typeof CoreWebVitalsAttemptRoleSchema
>;

// What a non-2xx CrUX field response means for the run: no data for this key,
// worth another attempt, permanently our fault, or unclassified.
export const CORE_WEB_VITALS_FIELD_RESPONSE_BRANCHES = [
  "no_data",
  "transient",
  "non_retryable",
  "error",
] as const;
export const CoreWebVitalsFieldResponseBranchSchema = z.enum(
  CORE_WEB_VITALS_FIELD_RESPONSE_BRANCHES,
);
export type CoreWebVitalsFieldResponseBranch = z.infer<
  typeof CoreWebVitalsFieldResponseBranchSchema
>;
export const CORE_WEB_VITALS_FIELD_RESPONSE_BRANCH = {
  NO_DATA: "no_data",
  TRANSIENT: "transient",
  NON_RETRYABLE: "non_retryable",
  ERROR: "error",
} as const satisfies Record<string, CoreWebVitalsFieldResponseBranch>;
